// src/pages/BrandDetail/components/PremiumMetricsSection.js
import { Bar } from 'react-chartjs-2';
import { useLanguage } from '../../../contexts/LanguageContext';
import './PremiumMetricsSection.css';

const PremiumMetricsSection = ({ premiumMetric }) => {
  const { translate } = useLanguage();

  if (!premiumMetric?.types) return null;

  const { types, premium_index } = premiumMetric;

  // Prepare and sort data
  const sortedData = (types || [])
    .map((type, index) => ({
      type: type || '',
      value: premium_index[index] || 0
    }))
    .filter(item => item.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 15);

  const chartData = {
    labels: sortedData.map(item => item.type),
    datasets: [{
      label: translate('brandDetailPage.premiumMetrics.premiumIndex') || 'Price Index',
      data: sortedData.map(item => item.value),
      backgroundColor: sortedData.map(item => {
        // Emerald for high value (<100), Indigo for premium (>=100)
        return item.value >= 100 ? 'rgba(99, 102, 241, 0.85)' : 'rgba(52, 211, 153, 0.85)';
      }),
      borderColor: sortedData.map(item => {
        return item.value >= 100 ? 'rgba(99, 102, 241, 1)' : 'rgba(52, 211, 153, 1)';
      }),
      borderWidth: 1,
      borderRadius: 4,
      barThickness: 24
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#334155',
        bodyColor: '#475569',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        cornerRadius: 8,
        boxPadding: 5,
        callbacks: {
          label: function (context) {
            return 'Index: ' + context.raw;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: true,
          drawBorder: false,
          color: 'rgba(241, 245, 249, 0.8)'
        },
        ticks: {
          font: { size: 11 },
          color: '#64748b'
        },
        border: { display: false }
      },
      y: {
        grid: { display: false },
        ticks: {
          font: { size: 12, weight: '500' },
          color: '#475569'
        },
        border: { display: false }
      }
    }
  };

  // Plugin to draw vertical annotation line at x=100
  const annotationPlugin = {
    id: 'annotationLine',
    afterDraw: (chart) => {
      const ctx = chart.ctx;
      const xAxis = chart.scales.x;
      const yAxis = chart.scales.y;

      const x = xAxis.getPixelForValue(100);
      if (x >= xAxis.left && x <= xAxis.right) {
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, yAxis.top);
        ctx.lineTo(x, yAxis.bottom);
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#94a3b8';
        ctx.setLineDash([]);
        ctx.stroke();

        // Draw label
        ctx.fillStyle = '#94a3b8';
        ctx.font = '11px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Market Avg (100)', x, yAxis.top - 10);
        ctx.restore();
      }
    }
  };

  return (
    <section className="glass-card premium-metrics-section-new">
      {/* Header */}
      <div className="premium-metrics-header-new">
        <div>
          <h2 className="font-serif premium-metrics-title">
            {translate('brandDetailPage.premiumMetrics.title') || 'Price Positioning'}
          </h2>
          <p className="premium-metrics-subtitle">
            Brand price vs. Market average (Index 100 = Market Avg)
          </p>
        </div>

        {/* Legend */}
        <div className="premium-legend-new">
          <div className="legend-item-new">
            <span className="legend-dot legend-dot-emerald"></span>
            <span className="legend-text">High Value (&lt;100)</span>
          </div>
          <div className="legend-item-new">
            <span className="legend-dot legend-dot-indigo"></span>
            <span className="legend-text">Premium (&gt;100)</span>
          </div>
        </div>
      </div>

      {/* Chart Container */}
      <div className="premium-chart-wrapper">
        {sortedData.length > 0 ? (
          <Bar
            data={chartData}
            options={chartOptions}
            plugins={[annotationPlugin]}
          />
        ) : (
          <div className="premium-no-data">
            No data available
          </div>
        )}
      </div>
    </section>
  );
};

export default PremiumMetricsSection;
